import { Commit, Module } from 'vuex'
import { ILoginState } from '@/store/login/types'
import { IRootState } from '@/store/types'
import { accountLoginRequest } from '@/service/login/login'
import { IAccount } from '@/service/login/loginType'
import { getKeyRequest } from '@/service/common/common'
import { JSEncrypt } from 'jsencrypt'
import {
  getFirstMenuPath,
  mapMenusToPermissions,
  mapMenusToRoutes
} from '@/utils/map-menus'
import {
  requestAccountByEmpNo,
  requestAccountMenu
} from '@/service/account/account'
import router from '@/router'
import { listWithEmp } from '@/service/main/promotion/access'
import {
  clearSessionCache,
  isCompleteSession,
  LoginSessionError,
  readSessionCache,
  SessionSnapshot,
  setTemporarySessionToken,
  writeSessionCache
} from '@/utils/session'

let activeLoginPromise: Promise<void> | null = null
let sessionRevision = 0

const createEmptyUserInfo = () => ({ roles: [] })

const clearSessionState = (commit: Commit) => {
  clearSessionCache()
  commit('changeToken', '')
  commit('changeUserInfo', createEmptyUserInfo())
  commit('changeUserMenu', [])
  commit('changeDepts', [])
}

const assertCurrentLogin = (revision: number) => {
  if (revision !== sessionRevision) {
    throw new LoginSessionError('登录流程已取消，请重新登录')
  }
}

const validateLoginResult = (loginResult: any) => {
  if (
    !loginResult ||
    typeof loginResult.token !== 'string' ||
    !loginResult.token.trim() ||
    !loginResult.empNo
  ) {
    throw new LoginSessionError('登录响应不完整，请稍后重试')
  }
}

const executeLogin = async (
  commit: Commit,
  payload: IAccount,
  revision: number
) => {
  try {
    const key = await getKeyRequest()
    assertCurrentLogin(revision)
    if (typeof key !== 'string' || !key.trim()) {
      throw new LoginSessionError('登录密钥获取失败，请稍后重试')
    }

    const encryptor = new JSEncrypt()
    encryptor.setPublicKey(key)
    const encryptedPassword = encryptor.encrypt(payload.password)
    if (!encryptedPassword) {
      throw new LoginSessionError('密码加密失败，请刷新页面后重试')
    }

    const loginResult = await accountLoginRequest({
      ...payload,
      password: encryptedPassword
    })
    assertCurrentLogin(revision)
    validateLoginResult(loginResult)

    const { token, empNo } = loginResult
    setTemporarySessionToken(token)

    const [accountInfo, accountMenu] = await Promise.all([
      requestAccountByEmpNo(empNo),
      requestAccountMenu(empNo)
    ])
    assertCurrentLogin(revision)

    if (
      !isCompleteSession({
        token,
        userInfo: accountInfo,
        userMenu: accountMenu
      })
    ) {
      throw new LoginSessionError('账号信息或权限菜单不完整，请联系管理员')
    }

    const depts = accountInfo.isManager == '1' ? await listWithEmp() : []
    assertCurrentLogin(revision)

    const session: SessionSnapshot = {
      token,
      userInfo: accountInfo,
      userMenu: accountMenu,
      depts: Array.isArray(depts) ? depts : []
    }

    writeSessionCache(session)
    commit('changeToken', session.token)
    commit('changeUserInfo', session.userInfo)
    commit('changeUserMenu', session.userMenu)
    commit('changeDepts', session.depts)
    if (!getFirstMenuPath()) {
      throw new LoginSessionError('未找到可访问菜单，请联系管理员')
    }
    await router.push('/')
  } catch (error) {
    if (revision === sessionRevision) {
      clearSessionState(commit)
    }
    throw error
  }
}

const loginModule: Module<ILoginState, IRootState> = {
  namespaced: true,
  state() {
    return {
      token: '',
      userInfo: createEmptyUserInfo(),
      userMenu: [],
      permissions: [],
      depts: []
    }
  },
  mutations: {
    changeToken(state, token: string) {
      state.token = token
    },
    changeUserInfo(state, userInfo: any) {
      state.userInfo =
        userInfo && typeof userInfo === 'object' && !Array.isArray(userInfo)
          ? {
              ...userInfo,
              roles: Array.isArray(userInfo.roles) ? userInfo.roles : []
            }
          : createEmptyUserInfo()
    },
    changeDepts(state, depts: any[]) {
      state.depts = depts
    },
    changeUserMenu(state, menuInfo: any) {
      const menus = Array.isArray(menuInfo) ? menuInfo : []
      const routes = mapMenusToRoutes(menus)
      routes.forEach((route) => {
        if (!route.name || !router.hasRoute(route.name)) {
          router.addRoute('main', route)
        }
      })

      const permissions = mapMenusToPermissions(menus)
      state.permissions = permissions
      state.userMenu = menus
    }
  },
  actions: {
    resetToken({ commit }) {
      sessionRevision += 1
      clearSessionState(commit)
      return 1
    },
    accountLoginAction({ commit }, payload: IAccount) {
      if (activeLoginPromise) return activeLoginPromise

      const revision = ++sessionRevision
      clearSessionState(commit)
      activeLoginPromise = executeLogin(
        commit,
        { ...payload },
        revision
      ).finally(() => {
        activeLoginPromise = null
      })
      return activeLoginPromise
    },
    loadLocalAction({ commit }) {
      const session = readSessionCache()
      if (!session) {
        clearSessionState(commit)
        return false
      }

      try {
        commit('changeToken', session.token)
        commit('changeUserInfo', session.userInfo)
        commit('changeUserMenu', session.userMenu)
        commit('changeDepts', session.depts)
        if (!getFirstMenuPath()) {
          throw new LoginSessionError('本地权限菜单无有效路由')
        }
        return true
      } catch (error) {
        clearSessionState(commit)
        return false
      }
    }
  }
}

export default loginModule
